import { onMounted, type ComputedRef } from "vue";
import { useTimer } from "@mkbabb/glass-ui/reactive";

import { useJourneyStore } from "@src/stores/useJourneyStore.ts";

/**
 * Fires `onStart()` exactly once per session, gated on perceptible
 * idle paint.
 *
 * Latch is journey-store-scoped (not mount-scoped) so the back-nav
 * path (speedtest → survey → back) doesn't auto-fire a second test on
 * the remount. Explicit Retake / Start clear the latch via
 * `journey.clearAutoStartLatch()`; back-nav does not.
 *
 * The gate composes onMounted + fonts.ready (bounded) + rAF + rIC +
 * visibility check, so the auto-start lands after the idle landing
 * paints — not before. Without this the LCP candidate would already
 * have flipped to the running readout before the user perceived the
 * landing.
 */
/** The shared opt-out token, used by both the localStorage key and the query param. */
const AUTO_START_KEY = "speedtest:auto-start";
const AUTO_START_OFF = "off";

/**
 * `true` when the session has explicitly opted OUT of auto-start via either
 * the localStorage kill-switch or the `?speedtest:auto-start=off` query param.
 *
 * Absent either signal, returns `false` — the product default (auto-run) is
 * byte-identically unchanged. Both reads are guarded so a missing `window` /
 * `localStorage` (SSR, private-mode, embedded contexts) cannot throw.
 */
function isAutoStartOptedOut(): boolean {
    try {
        if (
            typeof localStorage !== "undefined"
            && localStorage.getItem(AUTO_START_KEY) === AUTO_START_OFF
        ) {
            return true;
        }
    } catch {
        // localStorage access denied (private mode / sandboxed) — fall through.
    }
    try {
        if (
            typeof window !== "undefined"
            && new URLSearchParams(window.location.search).get(AUTO_START_KEY)
                === AUTO_START_OFF
        ) {
            return true;
        }
    } catch {
        // No `window.location` (SSR / non-browser) — fall through.
    }
    return false;
}

export function useAutoStart(opts: {
    shouldStart: ComputedRef<boolean>;
    onStart: () => void;
}): void {
    const journey = useJourneyStore();

    onMounted(async () => {
        // ── Auto-start opt-out (default behaviour UNCHANGED) ─────────────
        //    Auto-run stays the product semantic; ONLY an explicit opt-out
        //    signal suppresses the trigger. Two equivalent signals carry the
        //    same `= "off"` token:
        //
        //      1. `localStorage["speedtest:auto-start"] === "off"` — the
        //         documented operator kill-switch for the handoff dock.
        //      2. `?speedtest:auto-start=off` query param — the documented
        //         INSTRUMENTATION opt-out (same legal class as `?aurora=css`
        //         / `?mock-results`). The query-param fallback the pre-W
        //         A1-retrospective anticipated for this very key, so a
        //         navigated-to URL (e.g. the lhci honest at-rest collect)
        //         can score the page LOAD rather than a page mid-running a
        //         52 MB auto-started /garbage test. See
        //         DDR-AV-DEC9-HONEST-COLLECT.
        //
        //    Placed at the top of `onMounted` so the gate short-circuits
        //    before any rAF / rIC / visibility cost. Both reads are
        //    SSR/private-mode safe (guarded `typeof` + a swallowed parse). ──
        if (isAutoStartOptedOut()) {
            return;
        }

        // Journey-store-scoped latch survives the speedtest-view
        // remount the back-nav path triggers.
        if (journey.hasAutoStartedThisSession) return;
        if (!opts.shouldStart.value) return;

        // ── Fonts gate — bounded by a 3s ceiling. ──
        //    `useTimer` is scope-aware so an in-flight wait clears on
        //    unmount (a bare `setTimeout` would keep the resolve
        //    closure live past route-change). The race resolves on
        //    whichever fires first.
        if (typeof document !== "undefined" && document.fonts?.ready) {
            await Promise.race([
                document.fonts.ready,
                new Promise<void>((resolve) => {
                    useTimer(() => resolve(), 3000);
                }),
            ]);
        }

        // ── Paint gate — one rAF commits the idle frame. ──
        await new Promise<void>((resolve) =>
            requestAnimationFrame(() => resolve())
        );

        // ── Idle gate — one rIC defers to a quiet beat (or 100ms
        //    fallback when rIC is unavailable, e.g. Safari < 17). The
        //    fallback rides `useTimer` for the same scope-aware
        //    cleanup as the fonts gate above. ──
        await new Promise<void>((resolve) => {
            if (typeof requestIdleCallback === "function") {
                requestIdleCallback(() => resolve(), { timeout: 1000 });
            } else {
                useTimer(() => resolve(), 100);
            }
        });

        // ── Visibility gate — skip if the user has switched away
        //    during the wait. ──
        if (
            typeof document !== "undefined"
            && document.visibilityState !== "visible"
        ) {
            return;
        }

        // ── Re-check the predicate once more — operator may have
        //    toggled the store state during the wait. Preserves the
        //    one-fire-per-session invariant. ──
        if (journey.hasAutoStartedThisSession) return;
        if (!opts.shouldStart.value) return;

        journey.markAutoStarted();
        opts.onStart();
    });
}
